import * as FwDatas from "../FwDatas.ts";
import * as amplify from "../util/amplify.ts";


export class BaseCache<T>{
  refreshingPromise :Promise<T>;
  refreshingDate ?:Date;
  refresher :()=>Promise<T>
  datas :T;
  dependingCache:BaseCache<any>[] = []
  subscriptions : ((a:T)=>any)[] = [];
  constructor(refresher : ()=>Promise<T>){
    this.refresher = refresher;
    amplify.subscribe("familyScopeChange sessionExpired familyChange",()=>{
      this.kill();
    });
  }
  //return a promise that will return fresh datas
  async refresh() :Promise<T>{
    if(this.refreshingPromise){
      return this.refreshingPromise
    }
    try{
      var dependingPromise = this.dependingCache.map(cache=>cache.refresh())
      this.refreshingPromise = this.refresher();
      [this.datas] = await Promise.all([this.refreshingPromise,...dependingPromise])
      this.subscriptions.forEach(sub=>sub(this.datas))
      this.refreshingDate = new Date();
      return (this.datas)
    }finally{
      this.refreshingPromise = undefined;
    }
  }
  //return a promise that will update the data if needed or old
  async need(){
    //a refresh in progress means current datas are known to be stale
    if(this.refreshingPromise){
      return this.refresh();
    }
    if(this.refreshingDate && (new Date().getTime()-this.refreshingDate.getTime())<60000){
      return this.datas;
    }else{
      return this.refresh();
    }
  }
  //mark it for refresh needed
  invalidate(){
    this.refreshingDate = undefined;
  }
  //kill this cache
  kill(){
    this.datas = undefined;
    this.refreshingDate = undefined;
  }
  addDependency(... caches : BaseCache<any>[]){
    this.dependingCache.push(... caches);
  }
  subscribe(callback){
    this.subscriptions.push(callback);
  }
  unsubscribe(callback){
    var idx = this.subscriptions.indexOf(callback);
    if(idx!==-1){
      this.subscriptions.splice(idx,1);
    }
  }

}

export class ListCache<T extends {metaId?:string}> extends BaseCache<(T)[]>{
  constructor(api : ()=>Promise<T[]>, comparator :(a:T,b:T)=>number = undefined){
    super(async ()=>{
      var datas = await api();
      if(comparator){
        datas.sort(comparator);
      }
      return datas;
    })
    this.datas = [];

  }
  //get an element in the current cache out of date or not
  get(metaId :string){
    for(let i=0;i<this.datas.length;i++){
      if(this.datas[i].metaId===metaId) return this.datas[i]
    }
    return null;
  }
  override kill(){
    super.kill()
    this.datas = [];
  }
}

export class SyncCache<T extends {metaId?:string}> extends ListCache<T>{
  syncToken : string;
  constructor(api :(request:{syncToken :string|undefined})=>Promise<SyncBean<T>>, comparator :(a:T,b:T)=>number = undefined){
    super(async ()=>{
      //WARNING
      //it is not done with syncBean = await api();
      //because it would be done after the decoding of the request and we want to do it as decoding of the request
      var syncBean = await api({syncToken:this.syncToken})
      this.syncToken = syncBean.syncToken;
      FwDatas.mergeSyncBean(syncBean,this.datas);
      
      return this.datas;
    }, comparator)
  }

  override kill(){
    super.kill()
    this.syncToken = undefined;
  }
}

type PendingDelete = {
  ids :string[],
  commit :()=>Promise<unknown>
}
export type UndoableDelete = {
  ids :string[],
  undo :()=>void,
  commit :()=>Promise<void>
}

/**
 * Wraps a list cache so that its deletions can be undone, the server having no undo: a deferred
 * delete hides the items right away but only reaches the server on commit. The inner cache stays
 * the server state, the items pending deletion are filtered out of what this cache returns.
 */
export class UndoableCache<T extends {metaId?:string}> extends BaseCache<T[]>{
  inner :ListCache<T>;
  pending :PendingDelete[] = [];

  constructor(inner :ListCache<T>){
    super(async ()=>this.withoutPending(await inner.refresh()));
    this.inner = inner;
    this.datas = [];
  }

  private withoutPending(datas :T[]) :T[]{
    const ids = new Set(this.pending.flatMap(pending=>pending.ids));
    return ids.size?datas.filter(data=>!ids.has(data.metaId)):[...datas];
  }

  private update(){
    this.datas = this.withoutPending(this.inner.datas);
  }

  /**
   * Hides the items until the returned commit sends the deletion, or the undo shows them again.
   * @param commit the server call deleting the items
   */
  deferDelete(ids :string[], commit :()=>Promise<unknown>) :UndoableDelete{
    const pending :PendingDelete = {ids,commit};
    this.pending.push(pending);
    this.update();
    return {
      ids,
      undo:()=>{
        if(this.forget(pending)){
          this.update();
        }
      },
      commit:()=>this.commit(pending)
    };
  }

  /** false when the pending delete was already undone or committed */
  private forget(pending :PendingDelete) :boolean{
    const idx = this.pending.indexOf(pending);
    if(idx===-1){
      return false;
    }
    this.pending.splice(idx,1);
    return true;
  }

  private async commit(pending :PendingDelete){
    if(!this.forget(pending)){
      return;
    }
    //gone from the inner cache before the call: a second commit finds nothing to send, and a failed
    //delete has nothing better to show, the next sync brings the items back if they are still there
    this.inner.datas = this.inner.datas.filter(data=>pending.ids.indexOf(data.metaId)===-1);
    this.update();
    await pending.commit();
  }

  override invalidate(){
    super.invalidate();
    this.inner.invalidate();
  }

  override kill(){
    //the pending deletions belong to the family being left
    this.pending.forEach(pending=>pending.commit());
    this.pending = [];
    super.kill();
    this.datas = [];
    this.inner.kill();
  }
}

export class MetaCache4<A,B,C,D> extends BaseCache<[A,B,C,D]>{
  subCaches :[BaseCache<A>,BaseCache<B>,BaseCache<C>,BaseCache<D>];

  constructor(postHandle:(a:A,b:B,c:C,d:D)=>void,a :BaseCache<A>, b:BaseCache<B>, c:BaseCache<C>, d:BaseCache<D>){
    super(async ()=>{
      var data = await Promise.all([a.refresh(),b.refresh(),c.refresh(),d.refresh()])
      postHandle(...data)
      return data;
    })
    this.subCaches = [a,b,c,d];
  }
  override invalidate(){
    super.invalidate();
    this.subCaches.forEach(sub=>sub.refreshingDate = undefined)
  }
  override kill(){
    super.kill();
    this.subCaches.forEach(sub=>sub.kill())
  }
}

export class TranslationCache<T extends {metaId?:string},O> extends ListCache<T>{
  originalCache : BaseCache<O[]>;
  constructor(originalCache : BaseCache<O[]>, translator : (o:O[])=>T[]|Promise<T[]>){
    super(async ()=>{
      var data = await originalCache.refresh()
      return await translator(data);
    })
    this.addDependency(originalCache)
    this.originalCache = originalCache;
  }
  override invalidate(): void {
    super.invalidate()
    this.originalCache.invalidate()
  }
}

